import { ApolloLink, FetchResult, Observable, Operation } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { print } from 'graphql';
import { ApiCacheService } from './ApiCacheService';
import { getQueryTtl, isApiCacheEnabled } from './apiCacheConfig';

const shouldSkipCache = (operation: Operation): boolean =>
  !isApiCacheEnabled() || operation.getContext().skipApiCache === true;

const getCacheOperationName = (operation: Operation): string => {
  const definition = getMainDefinition(operation.query);

  if (definition.kind === 'OperationDefinition' && definition.name?.value) {
    return definition.name.value;
  }

  // Fallback so unnamed queries are still cached (keyed by document text).
  return `anonymous:${print(operation.query)}`;
};

export const createCachingApolloLink = (apiCacheService: ApiCacheService, cacheNamespace: string): ApolloLink =>
  new ApolloLink((operation, forward) => {
    const definition = getMainDefinition(operation.query);

    // Any successful mutation clears the entire API cache so every next query is fresh.
    if (definition.kind === 'OperationDefinition' && definition.operation === 'mutation') {
      return forward(operation).map(result => {
        if (!result.errors?.length) {
          apiCacheService.clear();
        }

        return result;
      });
    }

    if (definition.kind !== 'OperationDefinition' || definition.operation !== 'query') {
      return forward(operation);
    }

    if (shouldSkipCache(operation)) {
      return forward(operation);
    }

    const operationName = getCacheOperationName(operation);
    const variables = (operation.variables ?? {}) as Record<string, unknown>;
    const cacheKey = apiCacheService.buildKey(cacheNamespace, `${operationName}:${print(operation.query)}`, variables);
    const cachedResult = apiCacheService.get(cacheKey);

    if (cachedResult) {
      return Observable.of(cachedResult as FetchResult<Record<string, any>>);
    }

    return forward(operation).map(result => {
      if (!result.errors?.length) {
        apiCacheService.set(cacheKey, result as FetchResult<Record<string, any>>, {
          ttlMs: getQueryTtl(operation.operationName || operationName),
        });
      }

      return result;
    });
  });
